// Client-side email validation matching backend validation
// This ensures users get immediate feedback before API calls

const VALID_TLDS = [
  'com', 'org', 'net', 'edu', 'gov', 'mil', 'int',
  'co', 'io', 'ai', 'app', 'dev', 'tech', 'online',
  'info', 'biz', 'name', 'pro', 'xyz', 'me', 'tv',
  'us', 'uk', 'ca', 'au', 'de', 'fr', 'es', 'it', 'nl', 'be', 'ch', 'at', 'se', 'no', 'dk', 'fi', 'pl', 'cz', 'ie', 'pt', 'gr',
  'mx', 'br', 'ar', 'cl', 'co', 'pe', 've', 'ec', 'uy', 'py', 'bo', 'cr', 'pa', 'do', 'gt', 'hn', 'ni', 'sv', 'cu', 'jm', 'tt', 'bz', 'gy', 'sr', 'fk',
  'jp', 'cn', 'kr', 'in', 'sg', 'hk', 'tw', 'my', 'th', 'ph', 'id', 'vn', 'nz',
  'za', 'eg', 'ng', 'ke', 'ma', 'dz', 'tn', 'gh', 'et', 'ug', 'tz', 'zm', 'zw', 'mw', 'mz', 'ao', 'bw', 'na', 'sz', 'ls', 'mu', 'sc', 'km', 'dj', 'er', 'so', 'sd', 'ss', 'ly', 'td', 'ne', 'ml', 'bf', 'bj', 'tg', 'ci', 'lr', 'sl', 'gw', 'gn', 'sn', 'gm', 'mr', 'cv', 'st',
  'ru', 'ua', 'by', 'kz', 'uz', 'ge', 'am', 'az', 'md', 'ro', 'bg', 'rs', 'hr', 'ba', 'mk', 'al', 'me', 'xk', 'si', 'sk', 'hu', 'ee', 'lv', 'lt',
  'ae', 'sa', 'il', 'jo', 'lb', 'sy', 'iq', 'kw', 'qa', 'bh', 'om', 'ye', 'ir', 'af', 'pk', 'bd', 'lk', 'mm', 'kh', 'la',
  'tr', 'cy'
];

const COMMON_DOMAIN_TYPOS: Record<string, string> = {
  'gmail.cm': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.cpm': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmail.coom': 'gmail.com',
  'yahoo.co': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'outlook.co': 'outlook.com',
  'outlook.con': 'outlook.com',
};

export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
  suggestion?: string;
}

export const validateEmail = (email: string): EmailValidationResult => {
  // Empty email - don't show error until user starts typing
  if (!email || email.trim() === '') {
    return { isValid: true };
  }

  const trimmedEmail = email.trim();

  // Basic format check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmedEmail)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address',
    };
  }

  // Check for common typos and suggest correction
  const lowerEmail = trimmedEmail.toLowerCase();
  if (COMMON_DOMAIN_TYPOS[lowerEmail]) {
    const suggested = COMMON_DOMAIN_TYPOS[lowerEmail];
    return {
      isValid: false,
      error: `Invalid email domain. Did you mean ${suggested}?`,
      suggestion: suggested,
    };
  }

  // Extract domain and TLD
  const parts = trimmedEmail.toLowerCase().split('@');
  if (parts.length !== 2) {
    return {
      isValid: false,
      error: 'Please enter a valid email address',
    };
  }

  const domain = parts[1];
  const domainParts = domain.split('.');

  if (domainParts.length < 2) {
    return {
      isValid: false,
      error: 'Please enter a valid email address',
    };
  }

  // Get TLD (last part after the last dot)
  const tld = domainParts[domainParts.length - 1].toLowerCase();

  // Check if TLD is valid
  if (!VALID_TLDS.includes(tld)) {
    return {
      isValid: false,
      error: `"${tld}" is not a valid email domain. Please check your email address.`,
    };
  }

  // Additional validation: domain should not be too short or too long
  const domainWithoutTld = domainParts.slice(0, -1).join('.');
  if (domainWithoutTld.length < 1 || domainWithoutTld.length > 253) {
    return {
      isValid: false,
      error: 'Email domain is invalid',
    };
  }

  // Check that local part (before @) is not too long
  if (parts[0].length > 64) {
    return {
      isValid: false,
      error: 'Email address is too long',
    };
  }

  return { isValid: true };
};
